import * as React from 'react';
import ConfigApi, {
	CashManagementConfigurationResponse,
	CreatePolicyholderWorkflow,
	InvoiceConfiguration,
	OntologyResponse,
	ProductConfigurationResponse,
	Section,
	TenantConfigurationResponse,
} from '../api/ConfigApi';
import {
	BAD_USER_PERMISSION_ERRORS,
	handleUnauthorizedResponse,
} from '../api/http';
import IntegrationsApi, { AutofillCallResponse } from '../api/IntegrationsApi';
import { errorToast } from '../components/Toast';
import {
	AuthContext,
	AuthData,
	TenantAuthorizationData,
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
} from './auth';

export function withConfigContext<T>(Component: any) {
	return (props: Omit<T, 'configData'>) => (
		<AuthContext.Consumer>
			{(configData) => <Component {...props} configData={configData} />}
		</AuthContext.Consumer>
	);
}

export const ConfigContext = React.createContext<ConfigData>(null);

export const useConfigContext = () => React.useContext(ConfigContext);

export interface ConfigData {
	productConfigs: ProductConfigurationResponse[];
	policyholderConfig: any;
	policyholderCreateFields: Section[];
	policyholderCardConfig: any;
	createPolicyholderWorkflow: CreatePolicyholderWorkflow;
	paymentConfiguration: InvoiceConfiguration;
	cashManagementConfig: CashManagementConfigurationResponse;
	ontologyData: OntologyResponse;
	dataAutofillSequences: AutofillCallResponse[];
	setDataAutofillSequences: (
		dataAutofillSequences: AutofillCallResponse[],
	) => Promise<ConfigData>;
	setProductConfigs: (
		config: ProductConfigurationResponse[],
	) => Promise<ConfigData>;
	setPolicyholderConfig: (
		cardConfig: any,
		workflowConfig: any,
	) => Promise<ConfigData>;
	loadConfigFromUserData: (
		tenantAuthorizationData?: TenantAuthorizationData,
	) => Promise<void>;
	setPaymentConfiguration: (config: any) => Promise<ConfigData>;
	setOntologyData: (config: any) => Promise<ConfigData>;
	tenantConfiguration: TenantConfigurationResponse;
	setTenantConfiguration: (
		config: TenantConfigurationResponse,
	) => Promise<ConfigData>;
}

interface Props {
	authData: AuthData;
}

class ConfigProvider extends React.Component<Props, ConfigData> {
	constructor(props: any, context: any) {
		super(props);
		this.state = {
			productConfigs: null,
			policyholderConfig: null,
			policyholderCreateFields: null,
			policyholderCardConfig: null,
			createPolicyholderWorkflow: null,
			paymentConfiguration: null,
			cashManagementConfig: null,
			ontologyData: null,
			dataAutofillSequences: null,
			setDataAutofillSequences: this.setDataAutofillSequences,
			setProductConfigs: this.setProductConfigs,
			setPolicyholderConfig: this.setPolicyholderConfig,
			setPaymentConfiguration: this.setPaymentConfiguration,
			setOntologyData: this.setOntologyData,
			tenantConfiguration: null,
			setTenantConfiguration: this.setTenantConfiguration,
			loadConfigFromUserData: this.loadConfigFromUserData,
		};
	}

	public componentDidUpdate(prevProps: Props) {
		const initialAuthLoaded =
			!prevProps.authData.auth && this.props.authData.auth;
		const authTokenUpdated =
			this.props.authData.auth &&
			prevProps.authData.auth &&
			this.props.authData.auth.authorizationToken !==
				prevProps.authData.auth.authorizationToken;

		if (initialAuthLoaded || authTokenUpdated) {
			this.loadConfigFromUserData();
		}
	}

	public render() {
		return (
			<ConfigContext.Provider value={this.state}>
				{this.props.children}
			</ConfigContext.Provider>
		);
	}

	private setProductConfigs = (
		productConfigs: ProductConfigurationResponse[],
	) => {
		const promise = new Promise<ConfigData>((resolve) => {
			this.setState({ productConfigs }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setCashManagementConfig = (
		cashManagementConfig: CashManagementConfigurationResponse,
	) => {
		const promise = new Promise<ConfigData>((resolve) => {
			this.setState({ cashManagementConfig }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	// policyholderCardConfig contains the formatting of policyholder cards ('Card' in config studio)
	// policyholderWorkflowConfig contains the policyholder field details ('Dictionary' in config studio)
	private setPolicyholderConfig = (
		policyholderCardConfig: any,
		policyholderWorkflow: any,
	) => {
		const createPolicyholderWorkflow = policyholderWorkflow.workflows.filter(
			(workflow: any) => workflow.message === 'policyHolder.create.json',
		)[0];
		const policyholderCreateFields =
			createPolicyholderWorkflow.value.steps[0].sections;
		const promise = new Promise<ConfigData>((resolve) => {
			this.setState(
				{
					createPolicyholderWorkflow,
					policyholderCreateFields,
					policyholderCardConfig,
				},
				() => {
					resolve(this.state);
				},
			);
		});
		return promise;
	};

	// ontology data contains the form details for a policyholder ('Form' in config studio)
	private setOntologyData = (ontologyData: any) => {
		const promise = new Promise<ConfigData>((resolve) => {
			this.setState({ ontologyData }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setPaymentConfiguration = (paymentConfiguration: any) => {
		const promise = new Promise<ConfigData>((resolve) => {
			this.setState({ paymentConfiguration }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setTenantConfiguration = (
		tenantConfiguration: TenantConfigurationResponse,
	) => {
		const promise = new Promise<ConfigData>((resolve) => {
			this.setState({ tenantConfiguration }, () => {
				resolve(this.state);
			});
		});

		return promise;
	};

	private setDataAutofillSequences = (
		dataAutofillSequences: AutofillCallResponse[],
	) => {
		const promise = new Promise<ConfigData>((resolve) => {
			this.setState({ dataAutofillSequences }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private loadConfigFromUserData = async (
		tenantAuthorizationData?: TenantAuthorizationData,
	) => {
		// const { authData } = this.context;
		const { authData } = this.props;
		const userData = authData.auth;

		try {
			// get product configurations
			const productConfigPromise = await ConfigApi.getProductConfigs(
				tenantAuthorizationData?.authorizationToken ||
					userData.authorizationToken,
			);
			// get cash management configuration
			if (
				authData.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED]
			) {
				const cashManagementConfigPromise =
					await ConfigApi.getCashManagementConfig(
						tenantAuthorizationData?.authorizationToken ||
							userData.authorizationToken,
					);
				const cashManagementConfig = cashManagementConfigPromise.data;
				this.setCashManagementConfig(cashManagementConfig);
			}
			// get v2 product configurations
			const v2ProductConfigPromise = await ConfigApi.getV2ProductConfigs(
				tenantAuthorizationData?.tenantId || userData.tenantId,
				tenantAuthorizationData?.authorizationToken ||
					userData.authorizationToken,
			);
			// get ontology data (needed to retrieve policyholder configuration data)
			const ontologyPromise = await ConfigApi.getOntologyDefinitions(
				tenantAuthorizationData?.ontologyId || userData.ontologyId,
				tenantAuthorizationData?.authorizationToken ||
					userData.authorizationToken,
			);
			// get payment configuration
			const paymentConfigPromise = await ConfigApi.getPaymentConfiguration(
				tenantAuthorizationData?.authorizationToken ||
					userData.authorizationToken,
			);
			// get tenant info
			const tenantConfigPromise = await ConfigApi.getTenantConfiguration(
				tenantAuthorizationData?.authorizationToken ||
					userData.authorizationToken,
			);
			// fetch data autofill sequences (needed to control showing "Run Autofill" button)
			// set to empty array if endpoint fails
			let dataAutofillSequences: AutofillCallResponse[];
			try {
				const dataAutofillSequencesPromise =
					await IntegrationsApi.fetchAutofillSequences(
						tenantAuthorizationData?.authorizationToken ||
							userData.authorizationToken,
					);
				dataAutofillSequences = dataAutofillSequencesPromise.data.calls;
			} catch {
				dataAutofillSequences = [];
			}

			const productConfigs: any = productConfigPromise.data;

			const v2ProductConfigs = v2ProductConfigPromise.data;
			const ontology = ontologyPromise.data;
			const paymentConfig = paymentConfigPromise.data;
			const tenantConfig = tenantConfigPromise.data;
			this.setProductConfigs(productConfigs);
			this.setOntologyData(ontology);
			this.setPaymentConfiguration(paymentConfig);
			this.setTenantConfiguration(tenantConfig);
			this.setDataAutofillSequences(dataAutofillSequences);
			const v2GlobalConfig: any = v2ProductConfigs.filter(
				(config: any) => config.name === 'global',
			)[0];
			const policyholderCardWorkflowIds: string[] = Object.values(
				v2GlobalConfig.configuration.cardWorkflows,
			);
			const policyholderWorkflowIds: string[] = Object.values(
				v2GlobalConfig.configuration.workflows.policyHolder,
			);

			// get policyholder configuration
			const policyholderCardConfigPromise =
				await ConfigApi.getPolicyholderConfig(
					policyholderCardWorkflowIds,
					tenantAuthorizationData?.authorizationToken ||
						userData.authorizationToken,
				);
			const policyholderWorkflowConfigPromise =
				await ConfigApi.getPolicyholderConfig(
					policyholderWorkflowIds,
					tenantAuthorizationData?.authorizationToken ||
						userData.authorizationToken,
				);
			const policyholderCardConfig = policyholderCardConfigPromise.data;
			const policyholderWorkflowConfig = policyholderWorkflowConfigPromise.data;

			this.setPolicyholderConfig(
				policyholderCardConfig,
				policyholderWorkflowConfig,
			);
		} catch (error) {
			if (
				error.response &&
				BAD_USER_PERMISSION_ERRORS.includes(error.response.status)
			) {
				errorToast(
					`Error loading config: ${
						error.response.data.message
							? error.response.data.message
							: 'Permission denied, please contact your administrator.'
					}`,
				);

				await authData.clear();
			} else {
				await handleUnauthorizedResponse(error, authData);
			}
		}
	};
}

function withContext(Component: any) {
	return (props: Partial<Props>) => (
		<AuthContext.Consumer>
			{(authData) => <Component {...props} authData={authData} />}
		</AuthContext.Consumer>
	);
}

export default withContext(ConfigProvider);
